import { useContext } from 'react';
import type { ExecutionResult } from 'react-apollo';

import { useMutation } from '@apollo/client';
import type { DataProxy } from 'apollo-cache';

import { isAssignmentGradingInPublicEnabled } from 'bundles/grading-common';
import deleteGradeOverrideMutation from 'bundles/grading/api/DeleteGradeOverrideMutation.graphql';
import deleteGradeOverrideMutationV2 from 'bundles/grading/api/DeleteGradeOverrideMutationV2.graphql';
import type {
  DeleteGradeOverrideV2Mutation,
  DeleteGradeOverrideV2MutationVariables,
} from 'bundles/grading/api/__generated__/DeleteGradeOverrideMutationV2';
import type { DeleteGradeOverrideMutation, DeleteGradeOverrideMutationVariables } from 'bundles/grading/api/types';
import { ItemType } from 'bundles/grading/api/types';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import {
  generateSubmissionDataQueryVariables,
  updateCachedAssignmentGradeData,
  updateCachedAssignmentGradeDataV2,
} from 'bundles/grading/utils/apiUtils';
import {
  getNewSubmitterIdFromLegacyId,
  getScopedSubmitterIdFromRouteSubmitterId,
} from 'bundles/grading/utils/submitterUtils';

const useDeleteGradeOverrideMutation = () => {
  const { pageData, submissionData } = useContext(SGAPageContext);
  const routeSubmitterId = submissionData.data?.submitter?.id || '';
  const useApiV2 = isAssignmentGradingInPublicEnabled();

  const onDeleteGradeOverrideMutationComplete = (
    cache: DataProxy,
    { data }: ExecutionResult<DeleteGradeOverrideMutation>
  ) => {
    if (data?.GroupGrading_DeleteGradeOverride) {
      updateCachedAssignmentGradeData(
        cache,
        data,
        generateSubmissionDataQueryVariables(
          data?.GroupGrading_DeleteGradeOverride.submissionReference.groupId,
          data?.GroupGrading_DeleteGradeOverride.submissionReference.itemId,
          ItemType.StaffGradedAssignment,
          data?.GroupGrading_DeleteGradeOverride.submissionReference.submitterId
        )
      );
    }
  };

  const [mutate, { loading, error }] = useMutation<DeleteGradeOverrideMutation, DeleteGradeOverrideMutationVariables>(
    deleteGradeOverrideMutation,
    {
      context: { clientName: 'gatewayGql' },
      update: onDeleteGradeOverrideMutationComplete,
    }
  );

  const deleteGradeOverride = () => {
    return mutate({
      variables: {
        input: {
          submissionReference: {
            groupId: pageData.gradingGroupId || pageData.groupId || '',
            itemId: pageData.currentItemId,
            submitterId: getNewSubmitterIdFromLegacyId(submissionData.data?.submitter?.id || ''),
          },
        },
      },
    });
  };

  const onDeleteGradeOverrideMutationCompleteV2 = (
    cache: DataProxy,
    { data }: ExecutionResult<DeleteGradeOverrideV2Mutation>
  ) => {
    if (data?.GroupGrading_DeleteGradeOverrideV2) {
      updateCachedAssignmentGradeDataV2(cache, data);
    }
  };

  const [mutateV2, { loading: loadingV2, error: errorV2 }] = useMutation<
    DeleteGradeOverrideV2Mutation,
    DeleteGradeOverrideV2MutationVariables
  >(deleteGradeOverrideMutationV2, {
    context: { clientName: 'gatewayGql' },
    update: onDeleteGradeOverrideMutationCompleteV2,
  });

  const deleteGradeOverrideV2 = () => {
    return mutateV2({
      variables: {
        input: {
          courseId: pageData.courseId,
          itemId: pageData.currentItemId,
          scopedSubmitterId: getScopedSubmitterIdFromRouteSubmitterId(routeSubmitterId, pageData.gradingGroupId),
        },
      },
    });
  };

  if (useApiV2) {
    return {
      deleteGradeOverride: deleteGradeOverrideV2,
      loading: loadingV2,
      error: errorV2,
    };
  }

  return {
    deleteGradeOverride,
    loading,
    error,
  };
};

export default useDeleteGradeOverrideMutation;
